import { ReactNode, useMemo } from 'react';

import { OrganizationApiProvider } from '@gasbi/shared/web/Organization';
import { useDispatch } from 'react-redux';

import { useGetAdminOrganizationQuery } from '@/features/adminOrganization/adminOrganizationApi';
import {
  employeesApi,
  useActivateEmployeeMutation,
  useGetEmployeesAccessibleByOrganizationQuery,
  useGetEmployeesByRoleInfiniteQuery,
  useGetEmployeesByRoleQuery,
} from '@/features/employees';
import {
  mainOrganizationApi,
  roleEmployeesApi,
  useGetMainOrganizationRolesInfiniteQuery,
  useGetRoleEmployeesInfiniteQuery,
  useGetRoleEmployeesListQuery,
} from '@/features/mainOrganization/mainOrganizationApi';
import { useGetOrganizationByIdQuery } from '@/features/organization/organizationApi';
import {
  organizationsApi,
  useGetCachedOrganizationByIdSelector,
  useGetOrganizationRolesInfiniteQuery,
  useGetOrganizationsInfiniteQuery,
} from '@/features/organizations/organizationsApi';
import { permissionsApi } from '@/features/permissions/permissionsApi';
import {
  useAssignRoleMutation,
  useGetCachedRoleByIdSelector,
  useGetRoleByIdQuery,
  useGetSystemRolesQuery,
  useSaveRoleMutation,
  useSuspendRoleMutation,
} from '@/features/roles/hooks';
import {
  useGetRoleAvailablePermissionsByIdQuery,
  useGetRolePermissionsByIdQuery,
} from '@/features/roles/rolesApi';
import { RootRoleId } from '@/features/roles/types';
import { getRoleLabel, getRoleType, isRootRole } from '@/features/roles/utils';

interface Props {
  children: ReactNode;
  isMainOrganizationView?: boolean;
}

export const OrganizationApiContextWrapper = ({
  children,
  isMainOrganizationView = false,
}: Props) => {
  const dispatch = useDispatch();

  const value = useMemo(
    () => ({
      organizationsApi: {
        ...organizationsApi,
        useGetOrganizationByIdQuery,
        useGetOrganizationRolesInfiniteQuery,
        useGetCachedOrganizationByIdSelector,
        useGetOrganizationsInfiniteQuery,
      },
      mainOrganizationApi: {
        ...mainOrganizationApi,
        useGetMainOrganizationByIdQuery: () => useGetAdminOrganizationQuery(),
        useGetMainOrganizationRolesInfiniteQuery,
      },
      rolesApi: {
        useGetSystemRolesQuery,
        useGetRoleByIdQuery,
        useGetCachedRoleByIdSelector,
        useAssignRoleMutation,
        useSuspendRoleMutation,
        useSaveRoleMutation,
        useGetRolePermissionsByIdQuery,
        useGetRoleAvailablePermissionsByIdQuery,
      },
      employeesApi: {
        // ...employeesApi,
        useActivateEmployeeMutation,
        useGetEmployeesByRoleInfiniteQuery: isMainOrganizationView
          ? useGetRoleEmployeesInfiniteQuery
          : useGetEmployeesByRoleInfiniteQuery,
        useGetEmployeesByRoleQuery: isMainOrganizationView
          ? useGetRoleEmployeesListQuery
          : useGetEmployeesByRoleQuery,
        useGetEmployeesAccessibleByOrganizationQuery,
        useGetEmployeesInfiniteQuery: () => {},
        useGetDepartmentsIInfiniteQuery: () => {},
        useGetEmployeePositionsIInfiniteQuery: () => {},
      },
      subdivisionsApi: {
        // ...subdivisionsApi,
        useGetSubdivisionByIdQuery: () => ({
          data: [],
          isLoading: false,
        }),

        useGetSubdivisionsInfiniteQuery: () => {},
      },
      common: {
        RootRoleId,
        isRootRole,
        getRoleLabel,
        getRoleType,
      },
      utils: {
        invalidateOrgRolesTags: () =>
          dispatch(organizationsApi.util.invalidateTags(['Roles'])),
        invalidateMainOrgRolesTags: () =>
          dispatch(mainOrganizationApi.util.invalidateTags(['Roles'])),
        invalidateEmployeesByRoleTags: () => {
          dispatch(employeesApi.util.invalidateTags(['EmployeesByRole']));
          dispatch(roleEmployeesApi.util.invalidateTags(['EmployeesByRole']));
        },
        invalidateUserPermissionsTags: () =>
          dispatch(permissionsApi.util.invalidateTags(['UserPermissions'])),
      },
      getOrganizationURL: () => {},
      getMainOrganizationURL: () => {},
      validateCodeFn: () => {},
    }),

    [dispatch, isMainOrganizationView],
  );

  return (
    // @ts-ignore
    <OrganizationApiProvider value={value}>{children}</OrganizationApiProvider>
  );
};
